import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoginDialog } from '@/components/LoginDialog'

/* ─── Design tokens（与落地页一致） ─── */
const CEMENT = '#EBEBEB'
const CARBON = '#111111'
const MID = '#555555'
const PALE = '#E0E0E0'
const LIGHT = '#F4F4F4'

function PillBadge({ children, dark = false }: { children: React.ReactNode; dark?: boolean }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold tracking-wide uppercase"
      style={{
        background: dark ? 'rgba(255,255,255,0.12)' : 'rgba(17,17,17,0.07)',
        color: dark ? '#ffffff' : CARBON,
        border: dark ? '1px solid rgba(255,255,255,0.18)' : `1px solid ${PALE}`,
      }}
    >
      {children}
    </span>
  )
}

function SectionBar({ badge, index, dark = false }: { badge: string; index: string; dark?: boolean }) {
  return (
    <div className="flex items-center justify-between mb-8">
      <PillBadge dark={dark}>{badge}</PillBadge>
      <span
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: '56px',
          color: dark ? 'rgba(255,255,255,0.12)' : 'rgba(17,17,17,0.10)',
          letterSpacing: '-0.02em',
          lineHeight: 1,
        }}
      >
        {index}
      </span>
    </div>
  )
}

/* ─── Navigation（About 页专用，含首页锚点联动） ─── */
function Nav({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // 与首页功能条一致：功能/故事/定价/关于我们（About Us 当前高亮）
  const links = [
    { label: t('landing.nav.features'), href: '/#features' },
    { label: t('landing.nav.story'), href: '/#story' },
    { label: t('landing.nav.pricing'), href: '/#pricing' },
    { label: t('landing.nav.about'), href: '/about', active: true },
  ]

  return (
    <header className="sticky top-0 z-50" style={{ background: CEMENT, borderBottom: `1px solid ${PALE}` }}>
      <div className="max-w-7xl mx-auto px-6 h-[60px] flex items-center justify-between">
        <a href="/" style={{ fontFamily: "'Bebas Neue', sans-serif", color: CARBON, letterSpacing: '0.06em', fontSize: '22px' }}>
          {t('landing.brand')}
        </a>
        <nav className="hidden md:flex items-center gap-1 rounded-full px-2 py-1" style={{ background: 'rgba(17,17,17,0.06)', border: `1px solid ${PALE}` }}>
          {links.map((l) => (
            <a
              key={l.label}
              href={l.href}
              className="px-4 py-1.5 rounded-full text-sm font-medium transition-all"
              style={{ color: l.active ? '#ffffff' : MID, background: l.active ? CARBON : 'transparent' }}
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden md:flex items-center gap-4">
          <button className="text-sm font-medium" style={{ color: MID }} onClick={onLogin}>
            {t('landing.nav.login')}
          </button>
          <button className="rounded-full px-5 py-2 text-sm font-semibold transition-all hover:opacity-80" style={{ background: CARBON, color: '#ffffff' }} onClick={onLogin}>
            {t('landing.nav.getStarted')}
          </button>
        </div>
        <button className="md:hidden" style={{ color: CARBON }} onClick={() => setOpen(!open)} aria-label={t('landing.nav.menuAria')}>
          <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.8">
            {open ? (
              <>
                <line x1="5" y1="5" x2="17" y2="17" />
                <line x1="17" y1="5" x2="5" y2="17" />
              </>
            ) : (
              <>
                <line x1="4" y1="7" x2="18" y2="7" />
                <line x1="4" y1="12" x2="18" y2="12" />
                <line x1="4" y1="17" x2="18" y2="17" />
              </>
            )}
          </svg>
        </button>
      </div>
      {open && (
        <div className="md:hidden px-6 pb-5 flex flex-col gap-1 pt-3" style={{ borderTop: `1px solid ${PALE}` }}>
          {links.map((l) => (
            <a key={l.label} href={l.href} className="text-sm font-medium py-2 text-left rounded-lg px-2 transition-all" style={{ color: l.active ? CARBON : MID, background: l.active ? 'rgba(17,17,17,0.06)' : 'transparent', fontWeight: l.active ? 700 : 500 }}>
              {l.label}
            </a>
          ))}
          <button className="rounded-full px-5 py-2 text-sm font-semibold self-start mt-2" style={{ background: CARBON, color: '#fff' }} onClick={onLogin}>
            {t('landing.nav.getStarted')}
          </button>
        </div>
      )}
    </header>
  )
}

/* ─── /01 — Mission Hero ─── */
function Mission() {
  const { t } = useTranslation()
  const stats = [
    { n: t('landing.about.mission.stat1.n'), d: t('landing.about.mission.stat1.d') },
    { n: t('landing.about.mission.stat2.n'), d: t('landing.about.mission.stat2.d') },
    { n: t('landing.about.mission.stat3.n'), d: t('landing.about.mission.stat3.d') },
  ]
  return (
    <section style={{ background: CEMENT }} className="px-4 pt-8 pb-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-5">
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: MID }}>
            {t('landing.about.mission.kicker')}
          </p>
          <h1 className="leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(52px, 9vw, 130px)', color: CARBON, letterSpacing: '-0.01em' }}>
            {t('landing.about.mission.title1')}
            <br />
            {t('landing.about.mission.title2')}
          </h1>
          <p className="mt-4 text-lg font-medium" style={{ color: MID }}>
            {t('landing.about.mission.subtitle', { brand: t('landing.brand') })}
          </p>
        </div>
        <div className="rounded-3xl p-8 md:p-14 grid md:grid-cols-5 gap-10 items-start" style={{ background: CARBON }}>
          <div className="md:col-span-2 flex flex-col gap-6">
            <SectionBar badge={t('landing.about.mission.badge')} index="/01" dark />
            <p className="leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(38px, 5vw, 64px)', color: '#ffffff', letterSpacing: '-0.01em' }}>
              {t('landing.about.mission.quote')}
            </p>
            <div className="flex flex-col gap-3 mt-2">
              {stats.map((s) => (
                <div key={s.n} className="rounded-2xl px-5 py-4 flex items-center gap-4" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <span style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: '#ffffff', letterSpacing: '-0.02em', flexShrink: 0 }}>
                    {s.n}
                  </span>
                  <span className="text-sm leading-snug" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    {s.d}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-3 flex flex-col gap-5">
            <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('landing.about.mission.body1')}</p>
            <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('landing.about.mission.body2', { brand: t('landing.brand') })}</p>
            <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('landing.about.mission.body3', { brand: t('landing.brand') })}</p>
            <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.6)' }}>{t('landing.about.mission.body4')}</p>
            {/* 本地化：设计稿 Unsplash 图下载到 /images/about.jpg（零外网依赖） */}
            <div className="rounded-2xl overflow-hidden mt-2" style={{ background: '#1a1a1a', minHeight: 200 }}>
              <img src="/images/about.jpg" alt={t('landing.about.mission.imgAlt')} className="w-full h-full object-cover" style={{ opacity: 0.55 }} />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── /02 — Roots ─── */
function Roots() {
  const { t } = useTranslation()
  return (
    <section style={{ background: CEMENT }} className="px-4 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="rounded-3xl p-8 md:p-12" style={{ background: LIGHT, border: `1px solid ${PALE}` }}>
          <SectionBar badge={t('landing.about.roots.badge')} index="/02" />
          <h2 className="leading-none mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(40px, 6vw, 86px)', color: CARBON, letterSpacing: '-0.01em' }}>
            {t('landing.about.roots.title')}
          </h2>
          <p className="text-base mb-10 max-w-2xl" style={{ color: MID }}>{t('landing.about.roots.sub')}</p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-2xl p-8 flex flex-col gap-5" style={{ background: CARBON }}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl" style={{ background: 'rgba(255,255,255,0.1)' }}>🎨</div>
              <div>
                <p className="leading-tight mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: '#ffffff', letterSpacing: '0.01em' }}>{t('landing.about.roots.cardA.title')}</p>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)' }}>{t('landing.about.roots.cardA.body')}</p>
              </div>
              <div className="flex flex-wrap gap-2 mt-auto pt-2">
                {(t('landing.about.roots.cardA.pills', { returnObjects: true }) as unknown as string[]).map((tag: string) => (
                  <span key={tag} className="rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)', border: '1px solid rgba(255,255,255,0.1)' }}>{tag}</span>
                ))}
              </div>
            </div>
            <div className="rounded-2xl p-8 flex flex-col gap-5" style={{ background: '#ffffff', border: `1px solid ${PALE}` }}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl" style={{ background: LIGHT }}>🌿</div>
              <div>
                <p className="leading-tight mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '28px', color: CARBON, letterSpacing: '0.01em' }}>{t('landing.about.roots.cardB.title')}</p>
                <p className="text-sm leading-relaxed" style={{ color: MID }}>{t('landing.about.roots.cardB.body')}</p>
              </div>
              <div className="flex flex-wrap gap-2 mt-auto pt-2">
                {(t('landing.about.roots.cardB.pills', { returnObjects: true }) as unknown as string[]).map((tag: string) => (
                  <span key={tag} className="rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide" style={{ background: LIGHT, color: '#888', border: `1px solid ${PALE}` }}>{tag}</span>
                ))}
              </div>
            </div>
          </div>
          <div className="mt-4 rounded-2xl px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4" style={{ background: CEMENT, border: `1px solid ${PALE}` }}>
            <p className="leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(22px, 3vw, 36px)', color: CARBON, letterSpacing: '-0.01em' }}>{t('landing.about.roots.strip.quote')}</p>
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="w-8 h-8 rounded-full bg-[#111] flex items-center justify-center text-white text-xs font-bold">LP</div>
              <div>
                <p className="text-sm font-semibold" style={{ color: CARBON }}>{t('landing.about.roots.strip.author')}</p>
                <p className="text-xs" style={{ color: '#999' }}>{t('landing.about.roots.strip.role')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── /03 — Ecosystem ─── */
function Ecosystem() {
  const { t } = useTranslation()
  const product = (key: string) => ({
    name: t(`landing.about.ecosystem.${key}.name`),
    tag: t(`landing.about.ecosystem.${key}.tag`),
    emoji: t(`landing.about.ecosystem.${key}.emoji`),
    desc: t(`landing.about.ecosystem.${key}.desc`),
    pills: t(`landing.about.ecosystem.${key}.pills`, { returnObjects: true }) as unknown as string[],
  })
  const products = [
    { ...product('p1'), name: t('landing.brand'), dark: true, flagship: true },
    { ...product('p2'), dark: false, flagship: false },
    { ...product('p3'), dark: false, flagship: false },
  ]
  return (
    <section style={{ background: CEMENT }} className="px-4 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="rounded-3xl p-8 md:p-12" style={{ background: '#ffffff', border: `1px solid ${PALE}` }}>
          <SectionBar badge={t('landing.about.ecosystem.badge')} index="/03" />
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-10">
            <h2 className="leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(40px, 6vw, 86px)', color: CARBON, letterSpacing: '-0.01em' }}>
              {t('landing.about.ecosystem.title1')}
              <br />
              {t('landing.about.ecosystem.title2')}
            </h2>
            <p className="text-sm max-w-xs text-right hidden md:block" style={{ color: MID }}>{t('landing.about.ecosystem.sub')}</p>
          </div>
          <p className="text-sm mb-10 md:hidden" style={{ color: MID }}>{t('landing.about.ecosystem.sub')}</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.name} className="rounded-2xl p-7 flex flex-col gap-5 relative" style={{ background: p.dark ? CARBON : LIGHT, border: p.dark ? 'none' : `1px solid ${PALE}` }}>
                {p.flagship && (
                  <div className="absolute -top-3 left-6">
                    <span className="rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide" style={{ background: '#10B981', color: '#fff' }}>{t('landing.about.ecosystem.flagship')}</span>
                  </div>
                )}
                <div className="flex items-start justify-between">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl" style={{ background: p.dark ? 'rgba(255,255,255,0.1)' : '#ffffff' }}>{p.emoji}</div>
                  <span className="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide" style={{ background: p.dark ? 'rgba(255,255,255,0.08)' : 'rgba(17,17,17,0.06)', color: p.dark ? 'rgba(255,255,255,0.4)' : '#888', border: p.dark ? '1px solid rgba(255,255,255,0.1)' : `1px solid ${PALE}` }}>{p.tag}</span>
                </div>
                <div className="flex-1">
                  <p className="leading-tight mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', color: p.dark ? '#ffffff' : CARBON, letterSpacing: '0.01em' }}>{p.name}</p>
                  <p className="text-sm leading-relaxed" style={{ color: p.dark ? 'rgba(255,255,255,0.45)' : MID }}>{p.desc}</p>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {p.pills.map((tag) => (
                    <span key={tag} className="rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide" style={{ background: p.dark ? 'rgba(255,255,255,0.07)' : '#ffffff', color: p.dark ? 'rgba(255,255,255,0.4)' : '#888', border: p.dark ? '1px solid rgba(255,255,255,0.1)' : `1px solid ${PALE}` }}>{tag}</span>
                  ))}
                </div>
                <a href="#" className="text-xs font-semibold flex items-center gap-1 transition-opacity hover:opacity-60" style={{ color: p.dark ? 'rgba(255,255,255,0.5)' : MID }}>
                  {t('landing.about.ecosystem.learnMore')}
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <line x1="2" y1="6" x2="10" y2="6" />
                    <polyline points="7,3 10,6 7,9" />
                  </svg>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── CTA Banner ─── */
function CTABanner({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation()
  return (
    <section style={{ background: CEMENT }} className="px-4 py-6">
      <div className="max-w-7xl mx-auto">
        <div className="rounded-3xl p-10 md:p-16 flex flex-col md:flex-row items-center justify-between gap-8" style={{ background: CARBON }}>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: 'rgba(255,255,255,0.35)' }}>{t('landing.about.cta.kicker')}</p>
            <h2 className="leading-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(40px, 6vw, 80px)', color: '#ffffff', letterSpacing: '-0.01em' }}>
              {t('landing.about.cta.title1')}
              <br />
              {t('landing.about.cta.title2')}
            </h2>
          </div>
          <div className="flex flex-col items-center md:items-end gap-4 flex-shrink-0">
            <button className="rounded-full px-8 py-4 font-semibold text-base transition-all hover:opacity-90 whitespace-nowrap" style={{ background: '#ffffff', color: CARBON }} onClick={onLogin}>
              {t('landing.about.cta.button', { brand: t('landing.brand') })}
            </button>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('landing.about.cta.sub')}</p>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Footer（复用 landing.footer.*） ─── */
function Footer({ onLogin }: { onLogin: () => void }) {
  const { t } = useTranslation()
  return (
    <footer style={{ background: CARBON }} className="px-4 pt-14 pb-8">
      <div className="max-w-7xl mx-auto">
        <div className="w-full text-center leading-none mb-10 select-none" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 'clamp(56px, 12vw, 150px)', color: 'rgba(255,255,255,0.07)', letterSpacing: '0.1em' }}>
          {t('landing.brand')}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 py-8" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <div>
            <p className="mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', color: '#fff', letterSpacing: '0.06em' }}>{t('landing.brand')}</p>
            <p className="text-sm leading-relaxed max-w-xs" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('landing.footer.desc')}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'rgba(255,255,255,0.25)' }}>{t('landing.footer.navigation')}</p>
            <div className="flex flex-col gap-2.5">
              <a href="/#features" className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('landing.nav.features')}</a>
              <a href="/about" className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('landing.nav.aboutUs')}</a>
              <a href="/#pricing" className="text-sm transition-colors hover:text-white" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('landing.nav.pricing')}</a>
              <button onClick={onLogin} className="text-sm transition-colors hover:text-white text-left" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('landing.nav.getStarted')}</button>
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest mb-4" style={{ color: 'rgba(255,255,255,0.25)' }}>{t('landing.footer.connect')}</p>
            <div className="flex flex-col gap-2.5 mb-5">
              <a href="mailto:hi@klikpt.vip" className="text-sm hover:text-white transition-colors" style={{ color: 'rgba(255,255,255,0.4)' }}>hi@klikpt.vip</a>
              <span className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('landing.footer.coaches')}</span>
            </div>
            <div className="flex gap-3">
              {['IG', 'TW'].map((s) => (
                <a key={s} href="#" className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all hover:bg-white/20" style={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}>{s}</a>
              ))}
            </div>
          </div>
        </div>
        <div className="flex flex-col md:flex-row justify-between items-center gap-3 pt-6">
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.2)' }}>{t('landing.footer.copyright')}</p>
          <div className="flex gap-5">
            {['Privacy', 'Terms'].map((l) => (
              <a key={l} href="#" className="text-xs hover:text-white/50 transition-colors" style={{ color: 'rgba(255,255,255,0.2)' }}>{t(`landing.footer.${l.toLowerCase()}`)}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  )
}

/* ─── About 内容区块（可在落地页 /about 内复用，OPT-041） ─── */
export function AboutSections({ onLogin }: { onLogin: () => void }) {
  return (
    <>
      <Mission />
      <Roots />
      <Ecosystem />
      <CTABanner onLogin={onLogin} />
    </>
  )
}

/* ─── Page Root ─── */
export function AboutPage() {
  const [loginOpen, setLoginOpen] = useState(false)
  return (
    <div style={{ background: CEMENT, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Nav onLogin={() => setLoginOpen(true)} />
      <main>
        <AboutSections onLogin={() => setLoginOpen(true)} />
      </main>
      <Footer onLogin={() => setLoginOpen(true)} />
      <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} />
    </div>
  )
}
